import { compact } from 'lodash'
import moment from 'moment/moment'
import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

import { Item } from '../../models/Item'
import { Payee } from '../../models/Payee'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { e164ToPhone } from '../../utils/phone'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from './ContactAvatar'

type Props = {
    payee: Payee
    person: User
    metadata: {
        paymentsAmount: number
        lastJobDate: moment.Moment | null
        nextJobDate: moment.Moment | null
        unpaidJobsAmount: number
    }
    skillItems: Item[]
}

export const PayeeView = ({ payee, person, metadata, skillItems }: Props) => {
    const navigate = useNavigate()

    const otherDetailItems = (() => {
        const items: { id: string; title: string; content: string }[] = []

        if (person.sms) {
            items.push({ id: 'sms', title: 'Premium #', content: e164ToPhone(person.sms) ?? '' })
        }

        if (person.schedule !== undefined && person.schedule > 0) {
            const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
            const days = []
            for (let i = 0; i < 7; i++) {
                if ((person.schedule & (1 << i)) !== 0) days.push(weekDays[i])
            }
            if (days.length > 0) {
                items.push({ id: 'schedule', title: 'Calendar', content: days.join(', ') })
            }
        }

        if (person.role) {
            const displayRole =
                person.role === 'employee'
                    ? 'Staff'
                    : person.role === 'admin'
                      ? 'Administrator'
                      : person.role === 'contractor'
                        ? 'Limited'
                        : person.role === 'owner'
                          ? 'Owner'
                          : null

            if (displayRole) {
                items.push({ id: 'role', title: 'Role', content: displayRole })
            }
        }

        if (skillItems.length > 0) {
            items.push({
                id: 'services',
                title: 'Services',
                content: compact(skillItems.map((item) => item.name)).join(', '),
            })
        }

        return items
    })()

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-1">
                        <ContactAvatar user={person} className="size-[116px] text-[52px]" />

                        <div className="mt-4 flex flex-col items-center justify-start gap-y-2">
                            <Heading size="sm">{person.name}</Heading>

                            {person.phone && (
                                <Label size="lg" className="font-normal text-gray-600">
                                    {e164ToPhone(person.phone)}
                                </Label>
                            )}

                            {person.email && (
                                <Paragraph
                                    size="lg"
                                    className="text-primary-700 font-normal underline"
                                >
                                    {person.email}
                                </Paragraph>
                            )}
                        </div>

                        <Button
                            intent="secondary"
                            onClick={() => navigate(`/messages/${person.id}`)}
                            className="mt-6"
                        >
                            Chat
                        </Button>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        <BaseRecordView.IconField
                            icon="dollar.large"
                            title={currencyFormat(metadata.paymentsAmount)}
                            subtitle="Payments"
                        />

                        <BaseRecordView.IconField
                            icon="calendar.large"
                            title={
                                metadata.lastJobDate
                                    ? metadata.lastJobDate.format('MMM Do, YYYY')
                                    : '-'
                            }
                            subtitle="Last job"
                        />

                        <BaseRecordView.IconField
                            icon="calendar.large"
                            title={
                                metadata.nextJobDate
                                    ? metadata.nextJobDate.format('MMM Do, YYYY')
                                    : '-'
                            }
                            subtitle="Next job"
                        />

                        <BaseRecordView.IconField
                            icon="dollar2.large"
                            title={currencyFormat(metadata.unpaidJobsAmount)}
                            subtitle="Unpaid jobs"
                        />
                    </div>
                </Card>

                {person.location !== undefined &&
                    (person.location ?? '').length > 0 &&
                    person.latitude !== undefined &&
                    person.longitude !== undefined && (
                        <BaseRecordView.LocationSection
                            location={person.location}
                            latLong={{
                                latitude: person.latitude,
                                longitude: person.longitude,
                            }}
                        />
                    )}

                <Card size="sm" title="Date added">
                    <Paragraph size="md" className="mt-6 text-gray-600">
                        {moment(payee.createdDate).format('MMM Do, YYYY')}
                    </Paragraph>
                </Card>
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <BaseRecordView.NotesSection target={{ type: 'user', user: person }} />

                {otherDetailItems.length > 0 && (
                    <Card title="Other details">
                        <div className="mt-8 flex flex-col items-stretch justify-start">
                            {otherDetailItems.map(
                                ({ id, title, content }, otherDetailItemIndex) => (
                                    <Fragment key={id}>
                                        <div className="flex flex-col items-stretch justify-center gap-y-2">
                                            <Label size="lg">{title}</Label>
                                            <Paragraph size="md" className="text-gray-600">
                                                {content}
                                            </Paragraph>
                                        </div>

                                        {otherDetailItemIndex < otherDetailItems.length - 1 && (
                                            <Divider className="my-4" />
                                        )}
                                    </Fragment>
                                )
                            )}
                        </div>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
